import { collection, doc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Reminder emails for the Evaluations Summary page.
 *
 * Nothing here sends an email. Each reminder is written to `emailReminders` as
 * its own document with `sent: false`; the APEX n8n workflow polls for that flag,
 * sends the mail from hr@ruskmedia.com, and flips it to true. That split is
 * deliberate — the sending identity lives in n8n, not in this bundle.
 *
 * One document per email, never one per click. A manager owing feedback on three
 * reportees gets three documents and therefore three emails, each naming a
 * different reportee, and a failure on one cannot mark the other two as sent.
 *
 * Same contract as employeeAdmin.js: nothing throws. Every function returns a
 * result object so a failure can be reported in the modal instead of replacing
 * the whole app with the full-screen permission error.
 */

export const REMINDER_TYPES = {
  SELF_EVALUATION: 'pending-self-evaluation',
  MANAGER_REVIEW: 'pending-manager-review',
};

/** Human label per type, for the confirm dialog and the row tooltip. */
export const REMINDER_LABELS = {
  [REMINDER_TYPES.SELF_EVALUATION]: 'Self-evaluation reminder',
  [REMINDER_TYPES.MANAGER_REVIEW]: 'Manager feedback reminder',
};

/** Which stage each reminder type belongs to. Stages not listed get no button. */
export const REMINDER_TYPE_BY_STAGE = {
  pending: REMINDER_TYPES.SELF_EVALUATION,
  'manager-review': REMINDER_TYPES.MANAGER_REVIEW,
};

/** Firestore caps a batch at 500 writes; leave headroom, as employeeAdmin does. */
const BATCH_LIMIT = 400;

/** A reminder queued this recently counts as "just sent" and is warned about. */
export const RECENT_REMINDER_MS = 24 * 60 * 60 * 1000;

/** Drop keys whose value is undefined — Firestore rejects them outright. */
const definedOnly = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

const getUserId = (user) => user?.uid || user?.id || null;

const cleanEmail = (value) => String(value || '').trim().toLowerCase();

/** First name only — the templates greet with "Hi," so this is for the record, not the body. */
const displayName = (user) => (user?.name || user?.email || '').trim();

/**
 * Templates as supplied by HR.
 *
 * Changing the wording here only affects reminders queued *after* the change:
 * `subject` and `body` are rendered at queue time and stored on the Firestore
 * document, so anything already sitting in `emailReminders` will still send with
 * the text it was created with.
 */
export const renderSelfEvaluationReminder = () => ({
  subject: 'Reminder: complete your self-evaluation',
  body: [
    'Hi,',
    '',
    'Please complete your self-evaluation on the portal by the end of the day.',
    '',
    'Thanks,',
    'Kashish',
  ].join('\n'),
});

export const renderManagerReviewReminder = ({ reporteeName }) => ({
  subject: `Reminder: add feedback on ${reporteeName}'s self-evaluation`,
  body: [
    'Hi,',
    '',
    `Please add your feedback on the self-evaluation of your reportee ${reporteeName} on the portal by the end of the day.`,
    '',
    'Thanks,',
    'Kashish',
  ].join('\n'),
});

/**
 * Self-evaluation reminders: the employee is the recipient.
 *
 * @param {Array}  employees  the people in the Pending Self-Review stage
 * @param {Object} ctx        { fy, period, requestedBy }
 * @returns {{docs: Array, skipped: Array}} skipped carries { name, reason }
 */
export const buildSelfEvaluationReminders = (employees, { fy, period, requestedBy }) => {
  const docs = [];
  const skipped = [];

  (employees || []).forEach(employee => {
    const email = cleanEmail(employee.email);
    if (!email) {
      skipped.push({ name: displayName(employee) || 'Unnamed user', reason: 'no email address on their profile' });
      return;
    }
    const employeeId = getUserId(employee);
    const { subject, body } = renderSelfEvaluationReminder();
    docs.push(definedOnly({
      type: REMINDER_TYPES.SELF_EVALUATION,
      to: email,
      toName: displayName(employee),
      reporteeName: null,
      reporteeEmail: null,
      employeeId: employeeId || undefined,
      recipientId: employeeId || undefined,
      fy,
      period,
      subject,
      body,
      sent: false,
      sentAt: null,
      attempts: 0,
      requestedBy: requestedBy || undefined,
    }));
  });

  return { docs, skipped };
};

/**
 * Manager feedback reminders: the employee's manager is the recipient, and the
 * employee is named in the body.
 *
 * Manager resolution is id-first with an email fallback, matching the order used
 * by the notification Cloud Function (functions/index.js) and EvaluationRow —
 * `managerId` is what firestore.rules matches on, but plenty of real profiles
 * carry only `managerEmail`.
 *
 * @param {Array}  employees  the people in the Manager Feedback stage
 * @param {Array}  users      the full roster, for resolving managers
 * @param {Object} ctx        { fy, period, requestedBy }
 */
export const buildManagerReviewReminders = (employees, users, { fy, period, requestedBy }) => {
  const docs = [];
  const skipped = [];
  const roster = Array.isArray(users) ? users : [];

  (employees || []).forEach(employee => {
    const reporteeName = displayName(employee) || 'your reportee';
    const managerDoc = employee.managerId
      ? roster.find(u => getUserId(u) === employee.managerId)
      : null;
    const managerEmail = cleanEmail(managerDoc?.email || employee.managerEmail);

    if (!managerEmail) {
      skipped.push({
        name: reporteeName,
        reason: employee.managerEmail || employee.managerId
          ? 'their manager has no email address on file'
          : 'no manager is assigned',
      });
      return;
    }

    const { subject, body } = renderManagerReviewReminder({ reporteeName });
    docs.push(definedOnly({
      type: REMINDER_TYPES.MANAGER_REVIEW,
      to: managerEmail,
      toName: displayName(managerDoc) || managerEmail,
      reporteeName,
      reporteeEmail: cleanEmail(employee.email) || null,
      employeeId: getUserId(employee) || undefined,
      recipientId: getUserId(managerDoc) || undefined,
      fy,
      period,
      subject,
      body,
      sent: false,
      sentAt: null,
      attempts: 0,
      requestedBy: requestedBy || undefined,
    }));
  });

  return { docs, skipped };
};

/** Build the right reminders for a stage. Returns empty lists for stages with no reminder. */
export const buildRemindersForStage = (stageId, employees, users, ctx) => {
  const type = REMINDER_TYPE_BY_STAGE[stageId];
  if (type === REMINDER_TYPES.SELF_EVALUATION) return buildSelfEvaluationReminders(employees, ctx);
  if (type === REMINDER_TYPES.MANAGER_REVIEW) return buildManagerReviewReminders(employees, users, ctx);
  return { docs: [], skipped: [] };
};

/**
 * Write the queue documents. Batched, because a whole stage can be 150+ emails
 * and one round trip per document would take long enough to look broken.
 *
 * `createdAt` is a server timestamp: it is what the n8n workflow's age filter
 * reads, so a wrong client clock must not be able to hide a reminder from it.
 *
 * @returns {Promise<{ok: boolean, queued: number, message?: string}>}
 */
export const queueReminders = async (docs) => {
  const pending = Array.isArray(docs) ? docs : [];
  if (pending.length === 0) return { ok: true, queued: 0 };

  const remindersRef = collection(db, 'emailReminders');
  let queued = 0;

  try {
    for (let i = 0; i < pending.length; i += BATCH_LIMIT) {
      const batch = writeBatch(db);
      const chunk = pending.slice(i, i + BATCH_LIMIT);
      chunk.forEach(entry => batch.set(doc(remindersRef), { ...entry, createdAt: serverTimestamp() }));
      await batch.commit();
      queued += chunk.length;
    }
    return { ok: true, queued };
  } catch (error) {
    console.error('Could not queue reminder emails:', error);
    return {
      ok: false,
      queued,
      message: error.code === 'permission-denied'
        ? 'Firestore rejected the write — your admin role could not be confirmed. '
          + 'If the rules were changed recently, deploy them with `firebase deploy --only firestore:rules`.'
        : `Could not queue the reminders (${error.code || error.message}).`,
    };
  }
};

/** A Firestore Timestamp, an ISO string, or null → milliseconds, or null. */
const toMillis = (value) => {
  if (!value) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Collapse the reminder documents for one FY and period into per-employee
 * history, so a row can say "Reminded 2h ago" and the confirm dialog can warn
 * before anyone is emailed twice in a day.
 *
 * Keyed by `employeeId` — the person the reminder is *about* — so a manager
 * reminder shows against the reportee's row, which is the row it was sent from.
 *
 * @returns {Object} { [employeeId]: { lastQueuedAt: number|null, count, sentCount } }
 */
export const summariseReminderHistory = (reminderDocs) => {
  const history = {};

  (reminderDocs || []).forEach(entry => {
    const key = entry.employeeId;
    if (!key) return;
    const at = toMillis(entry.createdAt);
    const current = history[key] || { lastQueuedAt: null, count: 0, sentCount: 0 };
    history[key] = {
      lastQueuedAt: at !== null && (current.lastQueuedAt === null || at > current.lastQueuedAt)
        ? at
        : current.lastQueuedAt,
      count: current.count + 1,
      sentCount: current.sentCount + (entry.sent ? 1 : 0),
    };
  });

  return history;
};

/** "2h ago" / "just now" — short enough to sit under a table cell. */
export const formatRelativeTime = (millis, now = Date.now()) => {
  if (!millis) return null;
  const diff = Math.max(0, now - millis);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
};

/** Employees from `people` reminded within the last 24h, for the dialog's warning. */
export const findRecentlyReminded = (people, history, now = Date.now()) => (
  (people || []).filter(person => {
    const at = history?.[getUserId(person)]?.lastQueuedAt;
    return at && now - at < RECENT_REMINDER_MS;
  })
);
